/**
 * Recording the screen from this page, for anybody without the extension.
 *
 * The browser's own screen capture and MediaRecorder, with nothing installed.
 * What it cannot do is see clicks — a page is not told about clicks in another
 * tab or app — so the result is an ordinary file, handed to the same upload as
 * one dropped on the form. The worker already copes with what a browser
 * records: a WebM whose header carries no duration is measured by decoding.
 *
 * The browser is reached through `RecorderDeps`, so the session's own rules —
 * when it stops, what it hands on, what it lets go of — run under test.
 */

/** About 19 MB a minute: sharp enough for UI text, and ~25 minutes under the cap. */
export const RECORDING_BITS_PER_SECOND = 2_500_000;

/** Room left under the upload limit for the last chunk to land in. */
const HEADROOM_BYTES = 8 * 1024 * 1024;

/** Where a recording stops itself, so the file it makes is never refused for size. */
export function recordingStopBytes(maxUploadBytes: number): number {
  return maxUploadBytes - HEADROOM_BYTES;
}

export interface RecordingFormat {
  /** What MediaRecorder is asked for. */
  recorderMimeType: string;
  /** The plain type the file carries — the upload form checks this one. */
  fileMimeType: "video/webm" | "video/mp4";
  extension: "webm" | "mp4";
}

const FORMATS: RecordingFormat[] = [
  { recorderMimeType: "video/webm;codecs=vp9,opus", fileMimeType: "video/webm", extension: "webm" },
  { recorderMimeType: "video/webm;codecs=vp8,opus", fileMimeType: "video/webm", extension: "webm" },
  { recorderMimeType: "video/webm", fileMimeType: "video/webm", extension: "webm" },
  // Safari records MP4 and nothing else.
  { recorderMimeType: "video/mp4", fileMimeType: "video/mp4", extension: "mp4" },
];

export function chooseRecordingFormat(isTypeSupported: (type: string) => boolean): RecordingFormat | null {
  return FORMATS.find((format) => isTypeSupported(format.recorderMimeType)) ?? null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const two = (n: number) => String(n).padStart(2, "0");

/** "Screen recording 1 Nov 14.32.webm" — the project is named from this. */
export function recordingFileName(at: Date, extension: string): string {
  return `Screen recording ${at.getDate()} ${MONTHS[at.getMonth()]} ${two(at.getHours())}.${two(at.getMinutes())}.${extension}`;
}

/** What the person chose in the browser's share prompt, in words. */
export function describeSurface(surface: string | undefined): string {
  if (surface === "browser") return "a tab";
  if (surface === "window") return "a window";
  if (surface === "monitor") return "your whole screen";
  return "your screen";
}

/**
 * The sentence for a recording that would not start, or null when there is
 * nothing to say. Closing the share prompt is a NotAllowedError, the same as a
 * refusal, and somebody who closed it on purpose wants no message.
 */
export function recordingErrorMessage(error: unknown): string | null {
  if (error instanceof DOMException && error.name === "NotAllowedError") return null;
  return "Recording couldn't start. Check that this browser is allowed to record the screen, then try again.";
}

/** Whether this browser can record from the page at all. Phones cannot share a screen. */
export function canRecordInPage(): boolean {
  if (typeof navigator === "undefined" || typeof window === "undefined") return false;
  if (typeof navigator.mediaDevices?.getDisplayMedia !== "function") return false;
  if (typeof MediaRecorder === "undefined") return false;
  return !window.matchMedia?.("(pointer: coarse)").matches || window.matchMedia("(any-pointer: fine)").matches;
}

export interface RecordingOutcome {
  /** Null when there is nothing to use: cancelled, or nothing was recorded. */
  file: File | null;
  reason: "stopped" | "limit" | "cancelled" | "empty";
}

export interface RecordingSession {
  /** For the live preview. */
  stream: MediaStream;
  /** "a tab", "a window"… */
  surface: string;
  /** Whether the microphone is in it — false if it was asked for and refused. */
  microphone: boolean;
  stop(): void;
  cancel(): void;
}

export interface RecorderDeps {
  getDisplayMedia(constraints: DisplayMediaStreamOptions): Promise<MediaStream>;
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  isTypeSupported(type: string): boolean;
  createRecorder(stream: MediaStream, options: MediaRecorderOptions): MediaRecorder;
  combine(tracks: MediaStreamTrack[]): MediaStream;
  /** One track from several; `close` releases whatever did the mixing. */
  mixAudio(tracks: MediaStreamTrack[]): { track: MediaStreamTrack; close(): void };
  now(): number;
}

export function browserRecorderDeps(): RecorderDeps {
  return {
    getDisplayMedia: (constraints) => navigator.mediaDevices.getDisplayMedia(constraints),
    getUserMedia: (constraints) => navigator.mediaDevices.getUserMedia(constraints),
    isTypeSupported: (type) => MediaRecorder.isTypeSupported(type),
    createRecorder: (stream, options) => new MediaRecorder(stream, options),
    combine: (tracks) => new MediaStream(tracks),
    mixAudio: (tracks) => {
      // MediaRecorder takes one audio track; the tab's sound and the voice
      // over it are two, so they are summed here.
      const context = new AudioContext();
      const destination = context.createMediaStreamDestination();
      for (const track of tracks) context.createMediaStreamSource(new MediaStream([track])).connect(destination);
      return { track: destination.stream.getAudioTracks()[0]!, close: () => void context.close() };
    },
    now: () => Date.now(),
  };
}

export async function startPageRecording(
  options: {
    microphone: boolean;
    stopBytes: number;
    at?: Date;
    onProgress(progress: { seconds: number; bytes: number }): void;
    onFinished(outcome: RecordingOutcome): void;
  },
  deps: RecorderDeps = browserRecorderDeps(),
): Promise<RecordingSession> {
  const display = await deps.getDisplayMedia({ video: { frameRate: 30 }, audio: true });

  let mic: MediaStream | null = null;
  let mixer: { close(): void } | null = null;
  const release = () => {
    display.getTracks().forEach((track) => track.stop());
    mic?.getTracks().forEach((track) => track.stop());
    mixer?.close();
  };

  try {
    if (options.microphone) {
      // A refused microphone is not a refused recording: carry on without it.
      mic = await deps.getUserMedia({ audio: true }).catch(() => null);
    }

    const format = chooseRecordingFormat(deps.isTypeSupported);
    if (!format) throw new Error("This browser can't record video in a format Veyso accepts.");

    const audio = [...display.getAudioTracks(), ...(mic?.getAudioTracks() ?? [])];
    let audioTrack: MediaStreamTrack[] = audio;
    if (audio.length > 1) {
      const mixed = deps.mixAudio(audio);
      mixer = mixed;
      audioTrack = [mixed.track];
    }
    const recorded = deps.combine([...display.getVideoTracks(), ...audioTrack]);
    const recorder = deps.createRecorder(recorded, {
      mimeType: format.recorderMimeType,
      videoBitsPerSecond: RECORDING_BITS_PER_SECOND,
    });

    const chunks: Blob[] = [];
    let bytes = 0;
    let reason: RecordingOutcome["reason"] = "stopped";
    let finished = false;
    const startedAt = deps.now();
    const at = options.at ?? new Date();

    const finish = (why: RecordingOutcome["reason"]) => {
      if (finished) return;
      reason = why;
      if (recorder.state === "inactive") {
        // Never started, or already wound down: nothing more will arrive.
        settle();
      } else {
        recorder.stop();
      }
    };

    const settle = () => {
      if (finished) return;
      finished = true;
      release();
      if (reason === "cancelled") return options.onFinished({ file: null, reason });
      if (bytes === 0) return options.onFinished({ file: null, reason: "empty" });
      const file = new File(chunks, recordingFileName(at, format.extension), { type: format.fileMimeType });
      options.onFinished({ file, reason });
    };

    recorder.ondataavailable = (event) => {
      if (!event.data || event.data.size === 0) return;
      chunks.push(event.data);
      bytes += event.data.size;
      options.onProgress({ seconds: (deps.now() - startedAt) / 1000, bytes });
      if (bytes >= options.stopBytes) finish("limit");
    };
    recorder.onstop = settle;

    // The browser's own "Stop sharing" bar ends the video track.
    display.getVideoTracks()[0]?.addEventListener("ended", () => finish("stopped"));

    // A chunk a second keeps the size honest and loses at most a second if the tab dies.
    recorder.start(1000);

    const surface = display.getVideoTracks()[0]?.getSettings().displaySurface;
    return {
      stream: display,
      surface: describeSurface(surface),
      microphone: Boolean(mic?.getAudioTracks().length),
      stop: () => finish("stopped"),
      cancel: () => finish("cancelled"),
    };
  } catch (error) {
    release();
    throw error;
  }
}
